// Duyệt tài liệu: danh sách chờ duyệt (cũ nhất trước) + đã xử lý gần đây
import { useMemo, useState } from 'react';
import { Button, Input, Table, Tabs, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { Check, Eye, Search, X } from 'lucide-react';
import DocShell from './components/DocShell';
import { QuickInfoDrawer } from './components/QuickInfo';
import useDocActions from './components/useDocActions';
import useDocsStore from './store';
import { deptName, type Doc } from './data';
import { categoryPath, matches } from './logic';
import { StatePill, docLink, fmtDateTime, fmtEffective } from './utils';

export default function DocumentApproval() {
  const actions = useDocActions();
  const docs = useDocsStore(s => s.docs);
  const types = useDocsStore(s => s.types);
  const categories = useDocsStore(s => s.categories);
  const [tab, setTab] = useState<'pending' | 'done'>('pending');
  const [q, setQ] = useState('');
  const [info, setInfo] = useState<Doc | null>(null);

  const pending = useMemo(() => docs.filter(d => !d.deletedAt && d.status === 'pending').sort((a, b) => (a.submittedAt || '').localeCompare(b.submittedAt || '')), [docs]);
  const since = dayjs().subtract(30, 'day').toISOString();
  const done = useMemo(() => docs
    .map(d => ({ d, h: [...d.history].reverse().find(h => h.action === 'Duyệt & ban hành' || h.action === 'Từ chối') }))
    .filter(x => !x.d.deletedAt && x.h && x.h.at >= since)
    .sort((a, b) => b.h!.at.localeCompare(a.h!.at)), [docs, since]);

  const waitDays = (d: Doc) => dayjs().diff(dayjs(d.submittedAt), 'day');

  const base: ColumnsType<Doc> = [
    { title: 'Mã tài liệu', dataIndex: 'code', width: 190, render: (v, d) => <Link to={docLink(d.id)} className="font-medium text-[#357f96] hover:underline">{v}</Link> },
    {
      title: 'Tên tài liệu', dataIndex: 'title', width: 320,
      render: (v, d) => (
        <div>
          <Link to={docLink(d.id)} className="font-medium text-slate-800 hover:text-[#357f96]">{v}</Link>
          <div className="text-[11px] text-slate-400">{types.find(t => t.id === d.typeId)?.name} · {categoryPath(categories, d.categoryId)}{d.replacesId ? ' · Ban hành lại' : ''}</div>
        </div>
      ),
    },
    { title: 'Hiệu lực', key: 'eff', width: 170, render: (_v, d) => fmtEffective(d) },
    { title: 'Đơn vị ban hành', dataIndex: 'unitId', width: 150, render: v => deptName(v) },
    { title: 'Người gửi', dataIndex: 'createdByName', width: 150 },
  ];

  const pendingCols: ColumnsType<Doc> = [
    ...base,
    {
      title: 'Gửi duyệt', dataIndex: 'submittedAt', width: 160,
      render: (v, d) => <div><div className="text-[13px]">{fmtDateTime(v)}</div>{waitDays(d) >= 3 && <div className="text-[11px] font-medium text-red-500">Chờ {waitDays(d)} ngày</div>}</div>,
    },
    {
      title: 'Hành động', key: 'act', width: 220, align: 'center', fixed: 'right',
      render: (_v, d) => (
        <div className="flex items-center justify-center gap-2">
          <Tooltip title="Xem nhanh"><Button size="small" icon={<Eye size={14} />} onClick={() => setInfo(d)} /></Tooltip>
          {actions.perm.approve(d) && <Button size="small" danger icon={<X size={14} />} onClick={() => actions.reject(d)}>Từ chối</Button>}
          {actions.perm.approve(d) && <Button size="small" type="primary" icon={<Check size={14} />} onClick={() => actions.approve(d)}>Duyệt</Button>}
        </div>
      ),
    },
  ];

  const filteredPending = pending.filter(d => matches(q, d.title, d.code, d.createdByName));
  const filteredDone = done.filter(x => matches(q, x.d.title, x.d.code, x.d.createdByName));

  return (
    <DocShell title="Duyệt tài liệu" subtitle={`${pending.length} tài liệu đang chờ duyệt`}>
      {actions.modals}
      <div className="rounded-lg border border-slate-200 bg-white">
        <Tabs
          activeKey={tab} onChange={k => setTab(k as 'pending' | 'done')} className="px-4 [&_.ant-tabs-nav]:!mb-0"
          items={[
            { key: 'pending', label: <span>Chờ duyệt <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-xs text-amber-700">{pending.length}</span></span> },
            { key: 'done', label: 'Đã xử lý (30 ngày)' },
          ]}
        />
        <div className="border-y border-slate-100 bg-slate-50/60 p-3">
          <Input allowClear prefix={<Search size={15} className="text-slate-400" />} placeholder="Tên, mã tài liệu, người gửi…" value={q} onChange={e => setQ(e.target.value)} className="max-w-sm" />
        </div>
        {tab === 'pending' ? (
          <Table<Doc> rowKey="id" columns={pendingCols} dataSource={filteredPending} scroll={{ x: 1400 }}
            locale={{ emptyText: 'Không có tài liệu chờ duyệt 🎉' }} pagination={{ defaultPageSize: 20, showTotal: t => `${t} tài liệu` }} />
        ) : (
          <Table<{ d: Doc; h?: { at: string; by: string; action: string; note?: string } }>
            rowKey={x => x.d.id} dataSource={filteredDone} scroll={{ x: 1100 }} pagination={{ defaultPageSize: 20 }}
            locale={{ emptyText: 'Chưa có tài liệu được xử lý trong 30 ngày' }}
            columns={[
              { title: 'Mã tài liệu', key: 'code', width: 190, render: (_v, x) => <Link to={docLink(x.d.id)} className="font-medium text-[#357f96] hover:underline">{x.d.code}</Link> },
              { title: 'Tên tài liệu', key: 'title', render: (_v, x) => x.d.title },
              { title: 'Kết quả', key: 'res', width: 140, render: (_v, x) => <StatePill doc={x.d} /> },
              { title: 'Người xử lý', key: 'by', width: 160, render: (_v, x) => x.h?.by },
              { title: 'Thời gian', key: 'at', width: 150, render: (_v, x) => fmtDateTime(x.h?.at) },
              { title: 'Ghi chú', key: 'note', width: 260, render: (_v, x) => <span className="text-xs text-slate-500">{x.h?.note || '—'}</span> },
            ]}
          />
        )}
      </div>
      <QuickInfoDrawer doc={info} onClose={() => setInfo(null)} />
    </DocShell>
  );
}
